import * as React from "react";

import {
  FORM_CONTROL_DISABLED_CLASS,
  FORM_CONTROL_FOCUS_RING_CLASS,
  FORM_CONTROL_FOCUS_RING_INLINE_CLASS,
  FORM_CONTROL_HEIGHT_CLASS,
  FORM_CONTROL_INVALID_CLASS,
  FORM_CONTROL_NUMBER_INPUT_CLASS,
  FORM_CONTROL_PLACEHOLDER_CLASS,
  FORM_CONTROL_SURFACE_CLASS,
  FORM_CONTROL_TEXT_CLASS,
  FORM_CONTROL_VALUE_CLASS,
} from "@/components/ui/form-control";
import { cn } from "@/lib/utils";

export type InputProps = Omit<React.ComponentProps<"input">, "size"> & {
  /**
   * `default` — forced 44px form-control height (forms).
   * `inline` — caller controls height (e.g. deal-card value editor at 20px).
   */
  size?: "default" | "inline";
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size = "default", ...props }, ref) => {
    const isInline = size === "inline";

    return (
      <input
        type={type}
        className={cn(
          FORM_CONTROL_SURFACE_CLASS,
          "flex w-full rounded-md transition-colors-smooth",
          isInline ? "px-1.5 py-0" : "px-3 py-2",
          /** File input affordances */
          "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
          FORM_CONTROL_PLACEHOLDER_CLASS,
          FORM_CONTROL_DISABLED_CLASS,
          isInline ? FORM_CONTROL_FOCUS_RING_INLINE_CLASS : FORM_CONTROL_FOCUS_RING_CLASS,
          FORM_CONTROL_INVALID_CLASS,
          !isInline && FORM_CONTROL_TEXT_CLASS,
          !isInline && FORM_CONTROL_VALUE_CLASS,
          type === "number" && FORM_CONTROL_NUMBER_INPUT_CLASS,
          className,
          /** Default size: last so local height overrides cannot shrink form controls */
          !isInline && FORM_CONTROL_HEIGHT_CLASS,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
